import React, { useEffect, useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Stack,
    TextField,
    MenuItem,
    Chip,
    Typography,
    IconButton,
    Divider,
    Alert,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { WorkerCertification, CertCategory, CertReviewStatus } from '../../models/worker';
import {
    confirmCert,
    fetchCertById,
    rejectCert,
    updateCert,
    requestCertReupload,
    reextractCert,
    UpdateCertPayload,
} from '../../api/workerCertificationsApi';
import CertImageViewer from './CertImageViewer';

interface CertReviewDialogProps {
    open: boolean;
    cert: WorkerCertification | null;
    onClose: () => void;
    onChanged: (updated: WorkerCertification) => void;
}

const CONFIDENCE_THRESHOLD = 0.7;

const isLowConfidence = (cert: WorkerCertification, field: string): boolean => {
    const v = cert.aiConfidence?.[field];
    return typeof v === 'number' && v < CONFIDENCE_THRESHOLD;
};

const CertReviewDialog: React.FC<CertReviewDialogProps> = ({ open, cert, onClose, onChanged }) => {
    const [form, setForm] = useState<UpdateCertPayload>({});
    const [rejectMode, setRejectMode] = useState(false);
    const [rejectionReason, setRejectionReason] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const seedFormFromCert = (c: WorkerCertification) => {
        setForm({
            proposedCertName: c.proposedCertName ?? undefined,
            certCategory: c.certCategory ?? undefined,
            issuer: c.issuer ?? undefined,
            holderName: c.holderName ?? undefined,
            holderDob: c.holderDob ?? undefined,
            certNumber: c.certNumber ?? undefined,
            expiryDate: c.expiryDate ?? undefined,
            level: c.level ?? undefined,
        });
    };

    useEffect(() => {
        if (cert) {
            seedFormFromCert(cert);
            setRejectMode(false);
            setRejectionReason('');
            setError(null);
        }
        // seedFormFromCert is stable enough — only changes when cert changes
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cert]);

    if (!cert) return null;

    /**
     * Poll the cert row until its status leaves PENDING_EXTRACTION (i.e. the
     * Bull processor has handled the queued job). Returns the final row, or
     * null if it never settled within the timeout.
     */
    const pollUntilExtracted = async (
        certId: string,
        maxWaitMs = 15000,
        intervalMs = 1000,
    ): Promise<WorkerCertification | null> => {
        const start = Date.now();
        while (Date.now() - start < maxWaitMs) {
            await new Promise(r => setTimeout(r, intervalMs));
            try {
                const latest = await fetchCertById(certId);
                if (latest.status !== CertReviewStatus.PENDING_EXTRACTION) {
                    return latest;
                }
            } catch {
                // transient fetch failure — keep polling until timeout
            }
        }
        return null;
    };

    // Compare each editable field's current form value against the cert's
    // original value. Treat null/undefined/"" as equivalent so the buttons
    // don't go "dirty" just because the cert had null + we seeded the input
    // with ''.
    const norm = (v: unknown): string => (v === null || v === undefined ? '' : String(v));
    const editableFields: Array<keyof UpdateCertPayload> = [
        'proposedCertName', 'certCategory', 'issuer', 'holderName', 'holderDob',
        'certNumber', 'expiryDate', 'level',
    ];
    const isDirty = editableFields.some(
        f => norm(form[f]) !== norm(cert[f as keyof WorkerCertification]),
    );

    /**
     * Normalize errors coming back from the API. NestJS validation errors put
     * the message either as a string ("Bad Request") or as an array of strings
     * (per-field validation messages). Coerce both into a single string so the
     * <Alert> never receives a non-renderable object/array and crashes the
     * dialog.
     */
    const extractErrorMessage = (e: any, fallback: string): string => {
        const raw = e?.response?.data?.message ?? e?.message ?? fallback;
        if (Array.isArray(raw)) return raw.join('; ');
        if (typeof raw === 'string') return raw;
        try {
            return JSON.stringify(raw);
        } catch {
            return fallback;
        }
    };

    const handleSaveAndConfirm = async () => {
        setBusy(true);
        setError(null);
        try {
            const updated = await updateCert(cert.id, form);
            const confirmed = await confirmCert(updated.id);
            onChanged(confirmed);
            onClose();
        } catch (e: any) {
            setError(extractErrorMessage(e, 'Failed to save & confirm.'));
        } finally {
            setBusy(false);
        }
    };

    const handleSaveOnly = async () => {
        setBusy(true);
        setError(null);
        try {
            const updated = await updateCert(cert.id, form);
            onChanged(updated);
        } catch (e: any) {
            setError(extractErrorMessage(e, 'Failed to save.'));
        } finally {
            setBusy(false);
        }
    };

    const handleConfirm = async () => {
        setBusy(true);
        setError(null);
        try {
            const confirmed = await confirmCert(cert.id);
            onChanged(confirmed);
            onClose();
        } catch (e: any) {
            setError(extractErrorMessage(e, 'Failed to confirm.'));
        } finally {
            setBusy(false);
        }
    };

    const handleReject = async () => {
        if (!rejectionReason.trim()) {
            setError('Please enter a rejection reason.');
            return;
        }
        setBusy(true);
        setError(null);
        try {
            const rejected = await rejectCert(cert.id, rejectionReason.trim());
            onChanged(rejected);
            onClose();
        } catch (e: any) {
            setError(extractErrorMessage(e, 'Failed to reject.'));
        } finally {
            setBusy(false);
        }
    };

    const handleRequestReupload = async () => {
        setBusy(true);
        setError(null);
        try {
            const updated = await requestCertReupload(cert.id);
            onChanged(updated);
            onClose();
        } catch (e: any) {
            setError(extractErrorMessage(e, 'Failed to flag for re-upload.'));
        } finally {
            setBusy(false);
        }
    };

    const handleReextract = async () => {
        setBusy(true);
        setError(null);
        try {
            const queued = await reextractCert(cert.id);
            onChanged(queued);
            // POST only queues the extraction job; actual Gemini call finishes
            // ~1-3s later in the Bull processor. Poll the row until status
            // leaves PENDING_EXTRACTION, then refresh the form so the recruiter
            // sees the new values inline.
            const final = await pollUntilExtracted(cert.id);
            if (final) {
                onChanged(final);
                seedFormFromCert(final);
            }
        } catch (e: any) {
            setError(extractErrorMessage(e, 'Failed to re-extract.'));
        } finally {
            setBusy(false);
        }
    };

    const canConfirm = cert.status === CertReviewStatus.PENDING_REVIEW;
    const canReextract =
        cert.status !== CertReviewStatus.VERIFIED && !!cert.sourceFileUrl;
    // Backend rejects re-upload on VERIFIED rows ("Reject first"). Don't let
    // the user even click — disable the button to match the API constraint.
    const canRequestReupload = cert.status !== CertReviewStatus.VERIFIED;

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span>
                    Cert review · {cert.proposedCertName || '(unidentified)'}
                    <Chip
                        size="small"
                        label={cert.status}
                        color={
                            cert.status === CertReviewStatus.VERIFIED ? 'success'
                            : cert.status === CertReviewStatus.REJECTED ? 'error'
                            : cert.status === CertReviewStatus.PENDING_REVIEW ? 'warning'
                            : 'default'
                        }
                        sx={{ ml: 2 }}
                    />
                </span>
                <IconButton onClick={onClose}><CloseIcon /></IconButton>
            </DialogTitle>

            <DialogContent dividers>
                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, height: { md: '70vh' } }}>
                    <Box sx={{ minHeight: 400 }}>
                        <CertImageViewer certId={cert.id} sourceFileUrl={cert.sourceFileUrl ?? null} />
                    </Box>

                    <Stack spacing={2} sx={{ overflow: 'auto', pr: 1 }}>
                        {(cert.validationFlags?.length ?? 0) > 0 && (
                            <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ rowGap: 1 }}>
                                {cert.validationFlags!.map((f, i) => (
                                    <Chip key={i} size="small" color="warning" label={`⚠ ${f.code}${f.detail ? ` — ${f.detail}` : ''}`} />
                                ))}
                            </Stack>
                        )}

                        {cert.workerCorrectionText && (
                            <Alert severity="info">
                                <strong>Worker said:</strong> {cert.workerCorrectionText}
                            </Alert>
                        )}

                        {(cert.aiPotentialIssues?.length ?? 0) > 0 && (
                            <Alert severity="warning">
                                <strong>AI flagged:</strong> {cert.aiPotentialIssues!.join('; ')}
                            </Alert>
                        )}

                        {cert.aiReasoning && (
                            <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                AI reasoning: {cert.aiReasoning}
                            </Typography>
                        )}

                        <Divider />

                        <TextField
                            label="Cert name"
                            value={form.proposedCertName ?? ''}
                            onChange={e => setForm({ ...form, proposedCertName: e.target.value })}
                            fullWidth
                            size="small"
                            error={isLowConfidence(cert, 'proposed_cert_name')}
                            helperText={isLowConfidence(cert, 'proposed_cert_name') ? 'Low confidence' : undefined}
                        />

                        <TextField
                            label="Category"
                            value={form.certCategory ?? ''}
                            onChange={e => setForm({ ...form, certCategory: e.target.value as CertCategory })}
                            select
                            fullWidth
                            size="small"
                        >
                            <MenuItem value="">(none)</MenuItem>
                            {Object.values(CertCategory).map(c => (
                                <MenuItem key={c} value={c}>{c}</MenuItem>
                            ))}
                        </TextField>

                        <TextField
                            label="Issuer"
                            value={form.issuer ?? ''}
                            onChange={e => setForm({ ...form, issuer: e.target.value })}
                            fullWidth
                            size="small"
                            error={isLowConfidence(cert, 'issuer')}
                        />

                        <TextField
                            label="Holder name"
                            value={form.holderName ?? ''}
                            onChange={e => setForm({ ...form, holderName: e.target.value })}
                            fullWidth
                            size="small"
                            error={isLowConfidence(cert, 'holder_name')}
                        />

                        <Stack direction="row" spacing={2}>
                            <TextField
                                label="Cert number"
                                value={form.certNumber ?? ''}
                                onChange={e => setForm({ ...form, certNumber: e.target.value })}
                                fullWidth
                                size="small"
                            />
                            <TextField
                                label="Level"
                                value={form.level ?? ''}
                                onChange={e => setForm({ ...form, level: e.target.value })}
                                fullWidth
                                size="small"
                            />
                        </Stack>

                        <Stack direction="row" spacing={2}>
                            <TextField
                                label="Expiry date"
                                type="date"
                                value={form.expiryDate ?? ''}
                                onChange={e => setForm({ ...form, expiryDate: e.target.value })}
                                InputLabelProps={{ shrink: true }}
                                fullWidth
                                size="small"
                                error={isLowConfidence(cert, 'expiry_date')}
                            />
                        </Stack>

                        <TextField
                            label="Holder DOB"
                            type="date"
                            value={form.holderDob ?? ''}
                            onChange={e => setForm({ ...form, holderDob: e.target.value })}
                            InputLabelProps={{ shrink: true }}
                            size="small"
                        />

                        {rejectMode && (
                            <TextField
                                label="Rejection reason"
                                value={rejectionReason}
                                onChange={e => setRejectionReason(e.target.value)}
                                multiline
                                rows={2}
                                fullWidth
                                size="small"
                                autoFocus
                            />
                        )}
                    </Stack>
                </Box>
            </DialogContent>

            <DialogActions>
                {!rejectMode && (
                    <>
                        <Button onClick={onClose} disabled={busy}>Cancel</Button>
                        <Button onClick={handleReextract} disabled={busy || !canReextract}>
                            {busy && cert.status === CertReviewStatus.PENDING_EXTRACTION
                                ? 'Re-extracting…'
                                : 'Re-extract with AI'}
                        </Button>
                        <Box sx={{ flex: 1 }} />
                        <Button onClick={handleSaveOnly} disabled={busy || !isDirty}>Save</Button>
                        {canConfirm && (
                            <>
                                <Button onClick={handleSaveAndConfirm} disabled={busy || !isDirty} variant="outlined">
                                    Save &amp; confirm
                                </Button>
                                <Button onClick={handleConfirm} disabled={busy} variant="contained" color="success">
                                    Confirm
                                </Button>
                            </>
                        )}
                        <Button onClick={() => setRejectMode(true)} disabled={busy} color="error">
                            Reject…
                        </Button>
                    </>
                )}
                {rejectMode && (
                    <>
                        <Button onClick={() => setRejectMode(false)} disabled={busy}>Back</Button>
                        <Box sx={{ flex: 1 }} />
                        <Button onClick={handleRequestReupload} disabled={busy || !canRequestReupload}>
                            Request re-upload
                        </Button>
                        <Button onClick={handleReject} disabled={busy} variant="contained" color="error">
                            Reject
                        </Button>
                    </>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default CertReviewDialog;
